import React from 'react';
import { Sparkles } from 'lucide-react';

type Tpl = { type: string; label: string; sample: any };

const TEMPLATES: Tpl[] = [
  {
    type: 'readiness_widget',
    label: 'Индекс готовности',
    sample: {
      type: 'readiness_widget',
      score: 67,
      delta: 8,
      subscores: [
        { name: 'Резюме', value: 80 },
        { name: 'Активность', value: 65 },
        { name: 'Воронка', value: 55 },
      ],
    },
  },
  {
    type: 'match_ring',
    label: 'Match-score кольцо',
    sample: { type: 'match_ring', score: 78, role: 'Senior Product Manager', salary: 320000 },
  },
  {
    type: 'vacancy_card',
    label: 'Карточка вакансии',
    sample: {
      type: 'vacancy_card',
      title: 'Senior Product Manager',
      company: 'Avito',
      salary: 350000,
      location: 'Москва',
      employment: 'Гибрид',
      match: 78,
    },
  },
  {
    type: 'referral_card',
    label: 'Реферальная карточка',
    sample: {
      type: 'referral_card',
      title: 'Backend Engineer',
      company: 'ВТБ',
      salary: 280000,
      location: 'Москва',
      benefit: 'Реферальный бонус 50 000 ₽ автору рекомендации',
    },
  },
  {
    type: 'interview_coach_preview',
    label: 'ИИ-консультант (Олег/Анна)',
    sample: {
      type: 'interview_coach_preview',
      persona: 'oleg',
      topic: 'Как ответить на «расскажите о себе» на синиор-роль',
      sample:
        'Не пересказывай резюме. Назови позицию-цель, 2-3 главных результата и почему именно эта компания.',
    },
  },
  {
    type: 'ask_ai_screenshot',
    label: 'Скриншот Ask AI',
    sample: {
      type: 'ask_ai_screenshot',
      question: 'Перепиши буллет: «работал с командой над запуском нового продукта»',
      answer:
        'Запустил мобильное приложение для 50k пользователей за 4 месяца с командой из 6 человек: DAU 12 000, retention D7 — 38%.',
    },
  },
  {
    type: 'salary_chart',
    label: 'Зарплатные медианы',
    sample: {
      type: 'salary_chart',
      role: 'Senior Product Manager · Москва',
      p25: 260000,
      p50: 320000,
      p75: 420000,
    },
  },
  {
    type: 'compare_table',
    label: 'Сравнение До / После',
    sample: {
      type: 'compare_table',
      left: { title: 'Без РаботаМакс', items: ['Откликаешься вслепую', 'Сопроводительное руками', 'Гадаешь по фидбеку'] },
      right: { title: 'С РаботаМакс', items: ['Видишь match-score', 'ИИ-помощник подсказывает', 'Растишь индекс готовности'] },
    },
  },
  {
    type: 'cta_button',
    label: 'CTA-кнопка',
    sample: { type: 'cta_button', text: 'Открыть Индекс готовности', url: '/dashboard', subtitle: 'Бесплатно. Без карты.' },
  },
  {
    type: 'quest_list',
    label: 'Задания (квесты)',
    sample: {
      type: 'quest_list',
      title: 'Что прокачать на этой неделе',
      items: [
        { title: 'Дополнить опыт за 2023-2024', points: 12 },
        { title: 'Откликнуться на 5 вакансий', points: 8 },
        { title: 'Пройти 1 mock-интервью с Олегом', points: 15 },
      ],
    },
  },
  {
    type: 'hypothesis_bar',
    label: 'Карьерные гипотезы',
    sample: {
      type: 'hypothesis_bar',
      title: 'Карьерные гипотезы под профиль',
      items: [
        { role: 'Senior PM Fintech', count: 42, primary: true },
        { role: 'Head of Product', count: 18 },
        { role: 'Product Lead', count: 31 },
      ],
    },
  },
];

interface Props {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onInsert: (newValue: string) => void;
  value: string;
}

const BlockInserter: React.FC<Props> = ({ textareaRef, onInsert, value }) => {
  const insert = (tpl: Tpl) => {
    const block = '```rmblock\n' + JSON.stringify(tpl.sample, null, 2) + '\n```\n';
    const ta = textareaRef.current;
    if (!ta) {
      onInsert((value || '') + '\n\n' + block);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const needsBreakBefore = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
    const needsBreakAfter = after && !after.startsWith('\n\n') ? (after.startsWith('\n') ? '\n' : '\n\n') : '';
    const next = before + needsBreakBefore + block + needsBreakAfter + after;
    onInsert(next);
    requestAnimationFrame(() => {
      const pos = (before + needsBreakBefore + block).length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-blue-50">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 mb-2">
        <Sparkles size={12} />
        Вставить визуальный блок:
      </div>
      <div className="flex flex-wrap gap-1.5">
        {TEMPLATES.map((tpl) => (
          <button
            key={tpl.type}
            type="button"
            onClick={() => insert(tpl)}
            className="px-2.5 py-1 text-xs font-medium bg-white text-blue-700 border border-blue-200 rounded hover:bg-blue-100 hover:border-blue-300 transition-colors"
            title={tpl.type}
          >
            {tpl.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default BlockInserter;
